import { BookOpenText, Bot, Calculator, ClipboardCheck, Eye, FileSearch, Info, Layers, Mic, Plug, Scale, Search, ShieldCheck, Users, Video, Wallet } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { CtaPanel, DataTable, FaqSection, HeroActions, IconCardGrid, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, StepFlow } from '@/components/page/PageKit';
import { SourceList, VendorReviews } from '@/components/page/VendorReviews';
import { AI_RECRUITING_FAQS, AI_RECRUITING_REVIEWED, AI_RECRUITING_REVIEWED_LABEL, AI_TOOLS, CHOOSING_CRITERIA, PILOT_STEPS, SHORT_ANSWER, STAGES, type StageId } from '@/data/aiRecruitingSoftware';
import { articleSchema, faqSchema } from '@/seo/schemas';
import { useSEO } from '@/hooks/useSEO';

const URL = 'https://beatview.ai/guides/best-ai-recruiting-software';
const TITLE = `Best AI Recruiting Software for Agencies: ${AI_TOOLS.length} Tools (2026)`;
const DESCRIPTION = `${AI_TOOLS.length} AI recruiting tools for recruitment agencies compared by hiring stage: screening and interviews, sourcing, interview notes and agency ATS. Sourced from vendor sites.`;
const HEADLINE = 'The best AI recruiting software for recruitment agencies in 2026';

const STAGE_ICONS: Record<StageId, typeof Video> = { screening: Video, sourcing: Search, notes: Mic, ats: Layers };
const CRITERIA_ICONS = [Calculator, Eye, Users, Plug, Wallet, ShieldCheck];
const PILOT_ICONS = [ClipboardCheck, FileSearch, Scale, Bot];

export default function BestAiRecruitingSoftware() {
  useSEO({ title: TITLE, description: DESCRIPTION, canonicalUrl: URL, ogType: 'article' });

  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'AI recruiting software for recruitment agencies',
    numberOfItems: AI_TOOLS.length,
    itemListElement: AI_TOOLS.map((tool, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: { '@type': 'SoftwareApplication', name: tool.name, url: tool.website, applicationCategory: 'BusinessApplication', description: tool.bestFor },
    })),
  };

  return (
    <PageShell>
      <PageStructuredData name={HEADLINE} description={DESCRIPTION} url={URL} dateModified={AI_RECRUITING_REVIEWED} breadcrumbs={[{ name: 'Resources', url: 'https://beatview.ai/resources' }, { name: 'Best AI recruiting software' }]} />
      <StructuredData data={[articleSchema({ headline: HEADLINE, description: DESCRIPTION, url: URL, datePublished: AI_RECRUITING_REVIEWED, about: { '@type': 'Thing', name: 'AI recruiting software' } }), itemList, faqSchema(AI_RECRUITING_FAQS)]} />
      <article>
        <PageHero breadcrumbs={[{ name: 'Resources', href: '/resources' }, { name: 'Best AI recruiting software' }]} eyebrow={`Guide · Checked ${AI_RECRUITING_REVIEWED_LABEL}`} title={<>The best AI recruiting software <span className="bv-hero-highlight">for recruitment agencies in 2026</span></>}>
          <InShort label="The short answer">{SHORT_ANSWER}</InShort>
          <div className="mt-7"><HeroActions primaryLabel="Try Beatview free on one role" secondary={{ label: 'Jump to the comparison', href: '#comparison' }} note={`${AI_TOOLS.length} tools, each checked on the vendor’s own website on ${AI_RECRUITING_REVIEWED_LABEL}. Beatview publishes this guide and is one of the tools in it.`} /></div>
        </PageHero>

        <Section tone="wash" id="comparison" labelledBy="comparison-title">
          <SectionHeading id="comparison-title" eyebrow="At a glance" title={`${AI_TOOLS.length} AI recruiting tools for agencies, side by side`} description="Grouped by the hiring stage each tool takes over. Scroll sideways on smaller screens." />
          <Reveal delay={0.1} className="mt-10">
            <DataTable
              caption={`AI recruiting software for recruitment agencies compared, checked ${AI_RECRUITING_REVIEWED_LABEL}`}
              minWidth={1120}
              dense
              columns={['Tool', 'Stage', 'What the AI does', 'Agency fit', 'ATS integrations', 'Pricing']}
              rows={AI_TOOLS.map(tool => [
                <span key="n" className="flex flex-col gap-1">
                  <a href={`#tool-${tool.id}`} className="hover:text-bv-blue">{tool.name}</a>
                  {tool.isBeatview && <span className="w-fit rounded-full bg-bv-orange-soft px-2 py-0.5 text-[11px] font-semibold text-bv-coral">Our product</span>}
                </span>,
                STAGES.find(s => s.id === tool.stage)?.short,
                tool.aiDoes,
                tool.agencyFit,
                tool.atsFit,
                tool.pricing,
              ])}
            />
            <div className="mt-5 flex gap-3 rounded-2xl border border-bv-blue-line bg-bv-base p-4 text-[13.5px] leading-6 text-bv-ink2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-bv-blue" />
              <p><strong className="text-bv-ink">How we built this list:</strong> Beatview publishes this guide and makes one of the tools in it. Every other row comes from the vendor’s own website, read on {AI_RECRUITING_REVIEWED_LABEL} and linked under Sources. “Not stated” means the vendor’s pages did not say, not that the feature is missing.</p>
            </div>
          </Reveal>
        </Section>

        <Section tone="white" labelledBy="stages-title">
          <SectionHeading id="stages-title" eyebrow="Start from the stage" title="Pick AI for the hours your consultants lose most" description="“AI recruiting software” covers four different jobs. Most agencies need one or two of them, not all four." />
          <IconCardGrid
            className="mt-12"
            columns={2}
            items={STAGES.map(stage => ({
              icon: STAGE_ICONS[stage.id],
              title: stage.title,
              body: <>{stage.body}<span className="mt-3 block text-[14px] font-semibold text-bv-ink">{AI_TOOLS.filter(t => t.stage === stage.id).map(t => t.name).join(' · ')}</span></>,
            }))}
          />
        </Section>

        <Section tone="base" id="reviews" labelledBy="reviews-title">
          <SectionHeading id="reviews-title" eyebrow="The tools" title={`${AI_TOOLS.length} AI recruiting tools, reviewed for agencies`} description="Who each tool suits, what its AI does, and what to check before you buy." />
          <VendorReviews vendors={AI_TOOLS} strengthsLabel="What the AI does" />
        </Section>

        <Section tone="white" labelledBy="criteria-title">
          <SectionHeading id="criteria-title" eyebrow="How to choose" title="Six questions that separate the right tool from a good demo" />
          <IconCardGrid className="mt-12" items={CHOOSING_CRITERIA.map((c, i) => ({ icon: CRITERIA_ICONS[i % CRITERIA_ICONS.length], title: c.title, body: c.body, href: c.href, link: c.link }))} />
        </Section>

        <Section tone="dark" labelledBy="pilot-title">
          <SectionHeading tone="dark" id="pilot-title" eyebrow="Before you sign" title="Prove it on one live role in 30 days" description="A pilot on a real vacancy tells you more than any comparison page, including this one." />
          <div className="mt-12"><StepFlow tone="dark" steps={PILOT_STEPS.map((s, i) => ({ ...s, icon: PILOT_ICONS[i % PILOT_ICONS.length] }))} /></div>
        </Section>

        <FaqSection title="AI recruiting software: questions agencies ask" items={AI_RECRUITING_FAQS} idPrefix="ai-recruiting-faq" tone="base" />

        <Section tone="white" narrow id="sources" labelledBy="sources-title">
          <SectionHeading id="sources-title" eyebrow="Sources and method" title="Where every claim comes from" description="Only each vendor’s own pages (and, for Bullhorn integrations, Bullhorn’s marketplace listing). No review sites, no guesses." />
          <Reveal delay={0.1}><SourceList groups={AI_TOOLS.map(t => ({ name: t.name, sources: t.sources }))} reviewedLabel={AI_RECRUITING_REVIEWED_LABEL} /></Reveal>
        </Section>
      </article>

      <RelatedLinks title="Go deeper before you choose" tone="base" items={[
        { title: 'Recruitment agency software', body: 'The ATS and CRMs agencies run, and where AI screening fits beside them.', href: '/guides/recruitment-agency-software', icon: Layers },
        { title: 'What is an AI recruiting agent?', body: 'What an agent does on its own, and what it never decides.', href: '/guides/what-is-an-ai-recruiting-agent', icon: BookOpenText },
        { title: 'HireVue alternatives', body: 'Interview platforms compared on format, AI and published pricing.', href: '/alternatives/hirevue', icon: Scale },
        { title: 'Screening cost calculator', body: 'Estimate the hours your desk spends on first-pass screening.', href: '/screening-cost-calculator', icon: Calculator },
      ]} />
      <CtaPanel eyebrow="Bring a live vacancy" title={<>See what AI screening does <span className="bv-hero-highlight">on your next role.</span></>} description="Beatview’s Free plan covers one active job with unlimited candidates. Pro adds AI video interviews." secondary={{ label: 'How agencies use it', href: '/for-recruiting-agencies' }} />
    </PageShell>
  );
}
